// TrackingPage.tsx
import React, { useEffect, useRef, useState } from "react";
import {
  getDriverMapPoints,
  getUserDriverMapPoints,
  getOrderTrackingDetails,
} from "../ordertracking/core/_requests";
import Lottie from "lottie-react";

import deliveryAnimation from "../assests/js/delivery food splash.json";
import { useSearchParams, useParams } from "react-router-dom";
import {
  AppBar,
  Toolbar,
  Typography,
  Container,
  Card,
  CardContent,
  Box,
  Alert,
  Paper,
  IconButton,
  Button,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import logo from "../assests/image/logo.svg";
import car from "../assests/image/gps-navigation.png";
import chefIcon from "../assests/image/chefmarker.png";
import customerIcon from "../assests/image/usermarker.png";

interface LatLng {
  lat: number;
  lng: number;
}

type Mode =
  | "driver_to_chef"
  | "driver_to_customer"
  | "waiting_driver"
  | "picked_show_user_map"
  | "completed_delivery"
  | "order_ready"
  | "static"
  | string;

// Load Google Maps dynamically
const loadGoogleMaps = (): Promise<void> => {
  return new Promise((resolve) => {
    if ((window as any).google) {
      resolve();
      return;
    }

    const existingScript = document.getElementById("googleMaps");
    if (existingScript) {
      existingScript.addEventListener("load", () => resolve());
      return;
    }

    const script = document.createElement("script");
    script.id = "googleMaps";
    // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
    script.src = `https://maps.googleapis.com/maps/api/js?key=${process.env.REACT_APP_PLACES_API_KEY}`;
    script.async = true;
    script.onload = () => resolve();
    document.body.appendChild(script);
  });
};

const TrackingPage: React.FC = () => {
  const { orderId } = useParams();
  const [searchParams] = useSearchParams();
  const urlMode = searchParams.get("mode") || undefined;

  const mapRef = useRef<google.maps.Map | null>(null);
  const driverMarkerRef = useRef<google.maps.Marker | null>(null);
  const destinationMarkerRef = useRef<google.maps.Marker | null>(null);
  const directionsRendererRef =
    useRef<google.maps.DirectionsRenderer | null>(null);

  const [driver, setDriver] = useState<LatLng | null>(null);
  const [destination, setDestination] = useState<LatLng | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [pageLoading, setPageLoading] = useState(true);
  const [mode, setMode] = useState<Mode>(urlMode || "driver_to_chef");
  const [error, setError] = useState<string | null>(null);
  const [driverInfo, setDriverInfo] = useState<any>(null);
  const [orderData, setOrderData] = useState<any>(null);
  const [showDetails, setShowDetails] = useState(false);
  const [eta, setEta] = useState<string | null>(null);
  const [distance, setDistance] = useState<string | null>(null);

  const detailsRef = useRef<HTMLDivElement | null>(null);
  const toggleBtnRef = useRef<HTMLDivElement | null>(null);

  // Init Google map once
  useEffect(() => {
    loadGoogleMaps().then(() => {
      if (mapRef.current) return;

      const map = new (window as any).google.maps.Map(
        document.getElementById("trackingMap") as HTMLElement,
        { center: { lat: 0, lng: 0 }, zoom: 14 }
      );

      directionsRendererRef.current =
        new (window as any).google.maps.DirectionsRenderer({
          map,
          suppressMarkers: true,
          polylineOptions: { strokeColor: "#1976d2", strokeWeight: 5 },
        });

      mapRef.current = map;
    });
  }, []);

  // Close order details if clicked outside
  useEffect(() => {
    function handleOutsideClick(event: MouseEvent) {
      if (
        showDetails &&
        detailsRef.current &&
        !detailsRef.current.contains(event.target as Node) &&
        toggleBtnRef.current &&
        !toggleBtnRef.current.contains(event.target as Node)
      ) {
        setShowDetails(false);
      }
    }

    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, [showDetails]);

  // Main polling & flow logic
  useEffect(() => {
    if (!orderId) return;
    let cancelled = false;

    const fetchTrackingPoints = async () => {
      try {
        setLoading(true);
        setError(null);

        // 1) Always fetch order details first (gives orderstatus & coords & driverProfile)
        const orderRes = await getOrderTrackingDetails(orderId);
        if (!orderRes?.status || !orderRes.data) {
          // If order not found, show error
          setError(orderRes?.message || "Order not found");
          setLoading(false); setPageLoading(false);
          return;
        }
        if (cancelled) return;

        const order = orderRes.data.order;
        setOrderData(order);
        setDriverInfo(orderRes.data.driverProfile || null);

        // If delivered -> show delivered screen (no map)
        if (order.orderstatusId === 6) {
          setMode("completed_delivery");
          setDriver(null);
          setDestination(null);
          setLoading(false); setPageLoading(false);
          return;
        }

        // 2) Call chef-side tracking API to learn mode (may return special "picked_show_user_map")
        const chefRes = await getDriverMapPoints(orderId);

        if (!chefRes?.status || !chefRes.data) {
          // if chef API fails, fallback to static chef/destination
          setMode("static");
          setDriver(null);
          setDestination(
            order?.chef
              ? {
                lat: parseFloat(order.chef.latitude),
                lng: parseFloat(order.chef.longitude),
              }
              : null
          );
          setLoading(false); setPageLoading(false);
          return;
        }
        if (cancelled) return;

        const chefMode = chefRes.data.mode;
        if (chefMode === "completed_delivery") {
          setMode("completed_delivery");
          setDriver(null);
          setDestination(null);
          setLoading(false); setPageLoading(false);
          return;
        }


        // 3) Handle special "picked_show_user_map" or direct user mode
        // ⭐ CUSTOMER VIEW → load user tracking map
        if (urlMode === "driver_to_customer") {
          try {
            const userRes = await getUserDriverMapPoints(orderId);

            if (userRes?.status && userRes.data) {
              const driverPos = userRes.data.driver
                ? {
                  lat: parseFloat(userRes.data.driver.latitude),
                  lng: parseFloat(userRes.data.driver.longitude),
                }
                : null;

              const drop = userRes.data.drop
                ? {
                  lat: parseFloat(userRes.data.drop.latitude),
                  lng: parseFloat(userRes.data.drop.longitude),
                }
                : null;

              setDriver(driverPos);
              setDestination(drop);
              setMode("driver_to_customer");
              setLoading(false); setPageLoading(false);
            }
          } catch (err) {
            console.log("User map error", err);
          }
          return; // ⭐ VERY IMPORTANT
        }

        // ⭐ CHEF VIEW → stop tracking after pickup
        if (chefMode === "picked_show_user_map") {
          setMode("picked_show_user_map");
          setDriver(null);
          setDestination(null);
          setLoading(false); setPageLoading(false);
          return;
        }


        // 4) waiting_driver or order_ready (no driver yet) — show chef as destination, driver null
        if (chefMode === "waiting_driver" || chefMode === "order_ready") {
          setMode(chefMode);
          setDriver(null);
          setDestination(
            chefRes.data.destination
              ? {
                lat: parseFloat(chefRes.data.destination.latitude),
                lng: parseFloat(chefRes.data.destination.longitude),
              }
              : order?.chef
                ? {
                  lat: parseFloat(order.chef.latitude),
                  lng: parseFloat(order.chef.longitude),
                }
                : null
          );
          setLoading(false); setPageLoading(false);
          setError(null);
          return;
        }

        // 5) driver_to_chef - normal route (chefRes gives driver + destination)
        if (chefMode === "driver_to_chef" || chefMode === "driver_to_chef") {
          const driverPos = chefRes.data.driver
            ? {
              lat: parseFloat(chefRes.data.driver.latitude),
              lng: parseFloat(chefRes.data.driver.longitude),
            }
            : null;
          const dest = chefRes.data.destination
            ? {
              lat: parseFloat(chefRes.data.destination.latitude),
              lng: parseFloat(chefRes.data.destination.longitude),
            }
            : order?.chef
              ? {
                lat: parseFloat(order.chef.latitude),
                lng: parseFloat(order.chef.longitude),
              }
              : null;

          setDriver(driverPos);
          setDestination(dest);
          setMode("driver_to_chef");
          setLoading(false); setPageLoading(false);
          setError(null);
          return;
        }

        // fallback: set static chef destination
        setMode("static");
        setDriver(null);
        setDestination(
          chefRes.data?.destination
            ? {
              lat: parseFloat(chefRes.data.destination.latitude),
              lng: parseFloat(chefRes.data.destination.longitude),
            }
            : order?.chef
              ? {
                lat: parseFloat(order.chef.latitude),
                lng: parseFloat(order.chef.longitude),
              }
              : null
        );
        setLoading(false); setPageLoading(false);
      } catch (err: any) {
        setLoading(false); setPageLoading(false);
        setError(err?.message || "Failed to load tracking info");
      }
    };

    fetchTrackingPoints();
    const interval = setInterval(fetchTrackingPoints, 15000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [orderId]);

  // Update destination marker when destination changes
  useEffect(() => {
    if (!mapRef.current || !destination) return;

    const google = (window as any).google;
    const iconUrl = mode === "driver_to_customer" ? customerIcon : chefIcon;

    if (!destinationMarkerRef.current) {
      destinationMarkerRef.current = new google.maps.Marker({
        position: destination,
        map: mapRef.current,
        icon: {
          url: iconUrl,
          scaledSize: new google.maps.Size(60, 60),
        },
      });
    } else {
      destinationMarkerRef.current.setPosition(destination);
    }

    // adjust bounds to include driver (if present)
    const bounds = new google.maps.LatLngBounds();
    bounds.extend(destination);
    if (driver) bounds.extend(driver);
    mapRef.current.fitBounds(bounds);
  }, [destination, driver, mode]);

  // Update driver marker & route
  useEffect(() => {
    if (!mapRef.current) return;
    const google = (window as any).google;
    // Remove existing directions if driver becomes null
    if (!driver) {
      if (driverMarkerRef.current) {
        driverMarkerRef.current.setMap(null);
        driverMarkerRef.current = null;
      }
      // clear directions
      if (directionsRendererRef.current) {
        directionsRendererRef.current.setDirections({ routes: [] } as any);
      }
      setEta(null);
      setDistance(null);
      return;
    }

    if (!driverMarkerRef.current) {
      driverMarkerRef.current = new google.maps.Marker({
        position: driver,
        map: mapRef.current,
        icon: {
          url: car,
          scaledSize: new google.maps.Size(60, 60),
        },
      });
    } else {
      driverMarkerRef.current.setPosition(driver);
    }

    // Draw route only if destination exists and we want route
    if (destination) {
      const directionsService = new google.maps.DirectionsService();
      directionsService.route(
        {
          origin: driver,
          destination,
          travelMode: google.maps.TravelMode.DRIVING,
        },
        (result: any, status: any) => {
          if (status === "OK" && result) {
            directionsRendererRef.current?.setDirections(result);
            const leg = result.routes[0].legs[0];
            if (leg) {
              setEta(leg.duration.text);
              setDistance(leg.distance.text);
            }
          }
        }
      );
    }
  }, [driver, destination]);

  // Small UI helpers for overlay messages
  const MessageOverlay: React.FC<{ title: string; subtitle?: string; button?: { text: string; onClick: () => void } }> = ({
    title,
    subtitle,
    button,
  }) => {
    return (
      <Box
        sx={{
          position: "absolute",
          top: 110,
          left: "50%",
          transform: "translateX(-50%)",
          zIndex: 9999,
          width: { xs: "90%", sm: 420 },
        }}
      >
        <Card sx={{ p: 2, textAlign: "center" }}>
          <Typography variant="h6" fontWeight={700}>
            {title}
          </Typography>
          {subtitle && (
            <Typography variant="body2" mt={1} color="text.secondary">
              {subtitle}
            </Typography>
          )}
          {button && (
            <Box mt={2}>
              <Button variant="contained" onClick={button.onClick}>
                {button.text}
              </Button>
            </Box>
          )}
        </Card>
      </Box>
    );
  };

  // Delivered full-screen card
  // 🔥 FULL STOP: If delivery completed, NEVER show map
  if (mode === "completed_delivery") {
    return (
      <Box sx={{ height: "100vh", display: "flex", flexDirection: "column" }}>
        <AppBar position="static" sx={{ backgroundColor: "green" }}>
          <Toolbar>
            <img src={logo} alt="Logo" style={{ width: 30, marginRight: 10 }} />
            <Typography variant="h6" sx={{ fontWeight: "bold" }}>
              Order Delivered
            </Typography>
          </Toolbar>
        </AppBar>

        <Container
          sx={{
            flexGrow: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Card
            sx={{
              p: 3,
              maxWidth: 400,
              width: "100%",
              textAlign: "center",
              boxShadow: 4,
              borderRadius: 3,
            }}
          >
            <Lottie
              animationData={deliveryAnimation}
              autoplay
              loop={true}
              style={{ width: 220, height: 220, margin: "0 auto" }}
            />

            <Typography variant="h5" fontWeight="600" mt={2}>
              Delivery Successful! 🎉
            </Typography>

            {/* <Typography variant="body1" mt={1} color="text.secondary">
              Your delicious food has arrived.
              <br />
              Tracking is now complete ✔️
            </Typography> */}
          </Card>
        </Container>
      </Box>
    );
  }
  if (mode === "picked_show_user_map") {
    return (
      <Box sx={{ height: "100vh", display: "flex", flexDirection: "column" }}>
        <AppBar position="static" sx={{ backgroundColor: "green" }}>
          <Toolbar>
            <img src={logo} alt="Logo" style={{ width: 30, marginRight: 10 }} />
            <Typography variant="h6" sx={{ fontWeight: "bold" }}>
              Order Picked
            </Typography>
          </Toolbar>
        </AppBar>

        <Container
          sx={{
            flexGrow: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Card
            sx={{
              p: 3,
              maxWidth: 400,
              width: "100%",
              textAlign: "center",
              boxShadow: 4,
              borderRadius: 3,
            }}
          >
            {/*  Optional Lottie animation (use deliveryAnimation or a new one) */}
            <Lottie
              animationData={deliveryAnimation}
              autoplay
              loop={true}
              style={{ width: 220, height: 220, margin: "0 auto" }}
            />

            <Typography variant="h5" fontWeight="600" mt={2}>
              Driver Picked Your Order
            </Typography>

            {/* <Typography variant="body1" mt={1} color="text.secondary">
              Chef-side tracking has stopped.
              <br />
              Customer can now follow live tracking in their app.
            </Typography> */}
          </Card>
        </Container>
      </Box>
    );
  }



  return (
    <Box sx={{ height: "100vh", display: "flex", flexDirection: "column" }}>
      <AppBar position="static" sx={{ backgroundColor: "green" }}>
        <Toolbar>
          <img src={logo} alt="Logo" style={{ width: 30, marginRight: 10 }} />
          <Typography variant="h6">
            {mode === "driver_to_chef"
              ? "Tracking: Driver to Chef"
              : mode === "driver_to_customer"
                ? "Tracking: Driver to Customer"
                : mode === "waiting_driver"
                  ? "Tracking"
                  : mode === "picked_show_user_map"
                    ? "Order Picked"
                    : "Tracking"}
          </Typography>
        </Toolbar>
      </AppBar>

      <Container sx={{ flexGrow: 1, py: 2, position: "relative" }}>
        {/* Page loading overlay */}
        {pageLoading && (
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              zIndex: 99999,
            }}
          >
            <Card sx={{ p: 3 }}>
              <Typography fontWeight={600}>Loading live tracking...</Typography>
            </Card>
          </Box>
        )}
        {/* Overlay messages for waiting / order ready */}
        {mode === "waiting_driver" && (
          <MessageOverlay
            title="Driver Not Yet Assigned"
            subtitle="We are searching for a driver. We'll notify you when one accepts."
          />
        )}

        {mode === "order_ready" && (
          <MessageOverlay
            title="Order Ready for Pickup"
            subtitle="Your order is ready. Waiting for a driver to collect it."
          />
        )}

        {/* If order picked but user map not available, show overlay message */}
        {mode === "picked_show_user_map" && !destination && (
          <MessageOverlay
            title="Order picked up"
            subtitle="Driver has picked up the order and is on the way to the customer."
          />
        )}

        {/* Floating order details toggle */}
        {orderData && (
          <Box
            sx={{
              position: "absolute",
              top: { xs: 140, sm: 150, md: 180 },
              left: 20,
              zIndex: 9999,
            }}
          >
            <Paper
              elevation={4}
              ref={toggleBtnRef}
              sx={{
                p: 1.2,
                borderRadius: 3,
                background: "white",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: 1,
                maxWidth: 200,
              }}
              onClick={() => setShowDetails(!showDetails)}
            >
              <Typography sx={{ fontWeight: 700 }}>Order Details</Typography>
              <Typography
                sx={{
                  transition: "0.3s",
                  transform: showDetails ? "rotate(180deg)" : "rotate(0deg)",
                }}
              >
                ▼
              </Typography>
            </Paper>
          </Box>
        )}

        {/* Details panel */}
        {showDetails && orderData && (
          <Paper
            elevation={5}
            ref={detailsRef}
            sx={{
              position: "absolute",
              top: { xs: 70, sm: 90 },
              left: 20,
              width: { xs: "85%", sm: 260, md: 260 },
              maxHeight: "70vh",
              overflowY: "auto",
              borderRadius: 3,
              p: 2,
              background: "green",
              color: "white",
              zIndex: 99999,
            }}
          >
            <IconButton
              onClick={() => setShowDetails(false)}
              sx={{
                position: "absolute",
                top: 6,
                right: 6,
                color: "white",
                fontSize: "18px",
              }}
            >
              <CloseIcon />
            </IconButton>

            <Typography>
              <b>Order ID:</b> {orderData.orderId}
            </Typography>
            <Typography mt={1}>
              <b>Customer:</b> {orderData.user?.userName}
            </Typography>
            <Typography mt={1}>
              <b>Chef:</b> {orderData.chef?.name}
            </Typography>
            <Typography mt={1}>
              <b>Delivery Fee:</b> ${orderData.deliveryCharge}
            </Typography>

            {driverInfo && (
              <>
                <Typography mt={2}>
                  <b>Driver:</b> {driverInfo.name}
                </Typography>

                {driverInfo.delivery_driver_vechicle?.length > 0 && (
                  <>
                    <Typography mt={2}>
                      <b>Vehicle:</b>
                    </Typography>
                    <Typography>
                      Type: {driverInfo.delivery_driver_vechicle[0].vechicleType}
                    </Typography>
                    <Typography>
                      Model: {driverInfo.delivery_driver_vechicle[0].modal}
                    </Typography>
                    <Typography>
                      Make: {driverInfo.delivery_driver_vechicle[0].make}
                    </Typography>
                    <Typography>
                      Color: {driverInfo.delivery_driver_vechicle[0].color}
                    </Typography>
                    <Typography>
                      Plate: {driverInfo.delivery_driver_vechicle[0].licensePlateNumber}
                    </Typography>
                  </>
                )}
              </>

            )}
          </Paper>
        )}

        {/* Map container */}
        <Card sx={{ height: "100%" }}>
          <CardContent sx={{ height: "100%", p: 0 }}>
            {error && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {error}
              </Alert>
            )}
            <div
              id="trackingMap"
              style={{
                width: "100%",
                height: "100%",
                borderRadius: "8px",
              }}
            />
          </CardContent>
        </Card>
        {/* ETA Display (Top Pill Design) */}
        {eta && (
          <Box
            sx={{
              position: "absolute",
              top: 20,
              left: "50%",
              transform: "translateX(-50%)",
              zIndex: 9999,
              width: "auto",
              minWidth: { xs: "80%", sm: 280 },
            }}
          >
            <Paper
              elevation={6}
              sx={{
                py: 1.5,
                px: 3,
                textAlign: "center",
                borderRadius: 50, // Pill shape
                background: "rgba(255, 255, 255, 0.98)",
                backdropFilter: "blur(12px)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 2,
                border: "1.5px solid #2e7d32",
                boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "baseline", gap: 0.5 }}>
                <Typography variant="h6" fontWeight={900} color="#2e7d32">
                  {eta.split(" ")[0]}
                </Typography>
                <Typography variant="caption" fontWeight={700} color="#2e7d32" sx={{ textTransform: "uppercase" }}>
                  {eta.split(" ")[1] || "min"}
                </Typography>
              </Box>

              <Box sx={{ width: "1px", height: "24px", background: "#e0e0e0" }} />

              <Typography variant="body2" color="text.primary" fontWeight={700}>
                {distance}
              </Typography>
            </Paper>
          </Box>
        )}
      </Container>
    </Box>
  );
};

export default TrackingPage;
